<template>
  <ControlledReaderShell
    ref="readerShell"
    :textbook-id="textbookId"
    :resource-ready="resourceReady"
    @progress-change="handleProgress"
    @completed="$emit('completed', $event)"
  >
    <template #toolbar>
      <button type="button" class="pdf-tool" :disabled="rendering || zoom <= 0.6" @click="zoomOut">缩小</button>
      <span class="pdf-zoom">{{ Math.round(zoom * 100) }}%</span>
      <button type="button" class="pdf-tool" :disabled="rendering || zoom >= 2.4" @click="zoomIn">放大</button>
      <button type="button" class="pdf-tool" :disabled="rendering" @click="resetZoom">适应宽度</button>
    </template>

    <template #default="slotProps">
      <div ref="viewport" class="controlled-pdf">
        <div v-if="documentLoading" class="controlled-pdf__state">
          <span class="controlled-pdf__spinner"></span>
          <p>正在加载 PDF{{ loadingPercent > 0 ? `（${loadingPercent}%）` : '' }}...</p>
        </div>
        <div v-else-if="documentError" class="controlled-pdf__state is-error">
          <p>{{ documentError }}</p>
          <button type="button" class="pdf-tool is-primary" @click="loadDocument">重新加载</button>
        </div>
        <div v-else class="controlled-pdf__canvas-wrap">
          <canvas ref="canvas" class="controlled-pdf__canvas" :aria-label="`PDF 第 ${slotProps.currentPage} 页`"></canvas>
          <div v-if="rendering" class="controlled-pdf__rendering">正在渲染第 {{ slotProps.currentPage }} 页...</div>
        </div>
      </div>
    </template>
  </ControlledReaderShell>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import ControlledReaderShell from './ControlledReaderShell.vue'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.js',
  import.meta.url
).toString()

export default {
  name: 'ControlledPdfReader',
  components: {
    ControlledReaderShell
  },
  props: {
    textbookId: {
      type: [Number, String],
      required: true
    },
    fileUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      pdfDocument: null,
      loadingTask: null,
      renderTask: null,
      documentLoading: true,
      documentError: '',
      loadingPercent: 0,
      rendering: false,
      resourceReady: false,
      renderSequence: 0,
      currentPage: 0,
      zoom: 1,
      resizeTimer: null
    }
  },
  watch: {
    fileUrl() {
      this.loadDocument()
    },
    currentPage() {
      this.renderCurrentPage()
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.loadDocument()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) window.clearTimeout(this.resizeTimer)
    this.cancelRender()
    if (this.loadingTask && typeof this.loadingTask.destroy === 'function') {
      this.loadingTask.destroy()
    }
    if (this.pdfDocument && typeof this.pdfDocument.destroy === 'function') {
      this.pdfDocument.destroy()
    }
  },
  methods: {
    async loadDocument() {
      this.resourceReady = false
      if (!this.fileUrl) {
        this.documentLoading = false
        this.documentError = '缺少 PDF 文件地址'
        return
      }

      this.documentLoading = true
      this.documentError = ''
      this.loadingPercent = 0
      this.cancelRender()

      try {
        if (this.loadingTask && typeof this.loadingTask.destroy === 'function') {
          await this.loadingTask.destroy()
        }
        if (this.pdfDocument && typeof this.pdfDocument.destroy === 'function') {
          await this.pdfDocument.destroy()
        }

        this.loadingTask = pdfjsLib.getDocument({
          url: this.fileUrl,
          withCredentials: false
        })
        this.loadingTask.onProgress = ({ loaded, total }) => {
          this.loadingPercent = total > 0 ? Math.min(100, Math.round(loaded / total * 100)) : 0
        }
        this.pdfDocument = await this.loadingTask.promise
        this.$emit('document-loaded', { totalPages: this.pdfDocument.numPages })
        await this.renderCurrentPage()
      } catch (error) {
        if (error && error.name === 'RenderingCancelledException') return
        this.resourceReady = false
        this.documentError = this.formatLoadError(error)
      } finally {
        this.documentLoading = false
      }
    },
    handleProgress(progress = {}) {
      const page = Number(progress.current_page) || Number(progress.effective_page_start) || 1
      const pageChanged = page !== this.currentPage
      this.resourceReady = false
      this.currentPage = page
      if (!pageChanged) this.renderCurrentPage()
      this.$emit('progress-change', progress)
    },
    async renderCurrentPage() {
      if (!this.pdfDocument || !this.currentPage) return
      if (this.currentPage < 1 || this.currentPage > this.pdfDocument.numPages) {
        this.cancelRender()
        this.resourceReady = false
        this.documentError = `配置页码 ${this.currentPage} 超出 PDF 实际页数 ${this.pdfDocument.numPages}`
        return
      }

      this.cancelRender()
      const sequence = ++this.renderSequence
      const expectedPage = this.currentPage
      let currentRenderTask = null
      this.rendering = true
      this.documentError = ''

      try {
        const page = await this.pdfDocument.getPage(expectedPage)
        if (sequence !== this.renderSequence || expectedPage !== this.currentPage) return

        await this.$nextTick()
        if (sequence !== this.renderSequence || expectedPage !== this.currentPage) return

        const canvas = this.$refs.canvas
        const viewportElement = this.$refs.viewport
        if (!canvas || !viewportElement) return

        const baseViewport = page.getViewport({ scale: 1 })
        const availableWidth = Math.max(320, viewportElement.clientWidth - 48)
        const fitScale = availableWidth / baseViewport.width
        const viewport = page.getViewport({ scale: Math.max(0.25, fitScale * this.zoom) })
        const outputScale = Math.min(window.devicePixelRatio || 1, 2)
        const context = canvas.getContext('2d', { alpha: false })

        canvas.width = Math.floor(viewport.width * outputScale)
        canvas.height = Math.floor(viewport.height * outputScale)
        canvas.style.width = `${Math.floor(viewport.width)}px`
        canvas.style.height = `${Math.floor(viewport.height)}px`

        currentRenderTask = page.render({
          canvasContext: context,
          viewport,
          transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0],
          background: '#ffffff'
        })
        this.renderTask = currentRenderTask
        await currentRenderTask.promise

        if (sequence === this.renderSequence && expectedPage === this.currentPage) {
          this.resourceReady = true
        }
      } catch (error) {
        if (sequence === this.renderSequence && (!error || error.name !== 'RenderingCancelledException')) {
          this.resourceReady = false
          this.documentError = error.message || 'PDF 页面渲染失败'
        }
      } finally {
        if (this.renderTask === currentRenderTask) this.renderTask = null
        if (sequence === this.renderSequence) this.rendering = false
      }
    },
    cancelRender() {
      this.renderSequence += 1
      if (this.renderTask && typeof this.renderTask.cancel === 'function') {
        this.renderTask.cancel()
      }
      this.renderTask = null
      this.rendering = false
    },
    requestPage(page) {
      return this.$refs.readerShell ? this.$refs.readerShell.requestPage(page) : Promise.resolve(false)
    },
    zoomIn() {
      this.zoom = Math.min(2.4, Number((this.zoom + 0.2).toFixed(1)))
      this.renderCurrentPage()
    },
    zoomOut() {
      this.zoom = Math.max(0.6, Number((this.zoom - 0.2).toFixed(1)))
      this.renderCurrentPage()
    },
    resetZoom() {
      this.zoom = 1
      this.renderCurrentPage()
    },
    handleResize() {
      if (this.resizeTimer) window.clearTimeout(this.resizeTimer)
      this.resizeTimer = window.setTimeout(() => this.renderCurrentPage(), 180)
    },
    formatLoadError(error) {
      const message = error && error.message ? error.message : ''
      if (/cors|fetch|network|unexpected server response/i.test(message)) {
        return 'PDF 文件加载失败，请检查文件跨域访问权限或重新上传教材'
      }
      return message || 'PDF 文件加载失败'
    }
  }
}
</script>

<style scoped>
.controlled-pdf {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: 24px;
  background: #eef1f5;
}

.controlled-pdf__canvas-wrap {
  position: relative;
  width: max-content;
  min-width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.controlled-pdf__canvas {
  display: block;
  max-width: none;
  background: #fff;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.16);
}

.controlled-pdf__rendering {
  position: absolute;
  top: 18px;
  left: 50%;
  padding: 8px 14px;
  color: #fff;
  background: rgba(15, 23, 42, 0.78);
  border-radius: 6px;
  transform: translateX(-50%);
}

.controlled-pdf__state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #6b7280;
}

.controlled-pdf__state.is-error {
  color: #b42318;
}

.controlled-pdf__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #dbeafe;
  border-top-color: #1677ff;
  border-radius: 50%;
  animation: pdf-spin 0.8s linear infinite;
}

.pdf-tool {
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
}

.pdf-tool:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pdf-tool.is-primary {
  color: #fff;
  border-color: #1677ff;
  background: #1677ff;
}

.pdf-zoom {
  min-width: 48px;
  text-align: center;
  color: #4b5563;
}

@keyframes pdf-spin {
  to { transform: rotate(360deg); }
}
</style>
